import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  FileText,
  Activity,
  Calendar,
  ChevronRight,
  Shield,
  Filter,
  Building2,
} from 'lucide-react';
import axios from 'axios';
import { claimsmonthly, claimyears } from '../BaseURL';
// ClaimsDetails is left in place but no longer used from here — see the note at
// the drill-down below. ClaimsMenu is the replacement that reads both claim
// models and matches the Claims Menu layout.
import ClaimsMenu from './ClaimsMenu';
import { BRAND } from '../../brand';

// ============================================================================
//  Claims Dashboard
// ============================================================================
//
//  COLOUR. Every month card used to draw from a list of twelve unrelated
//  multi-stop gradients — Purple Haze, Forest Lake, Silver Cloud — picked by
//  array index. The colour therefore encoded nothing except a month's position
//  in the list, while shouting loudly enough to be the first thing you saw. The
//  header stacked three more gradients on top of that, in a fourth and fifth
//  hue. The screen now uses the shared brand tokens: navy for structure, green
//  for the accent, and colour reserved for the one thing worth spotting at a
//  glance (the peak month) and for telling the specialty bars apart.
//
//  WIDTH. The cards carried `<Grid item xs={12} sm={6} lg={4}>`, which should
//  mean three per row — but MUI 7 ignores that API outright, so all six months
//  crammed into a single row and each card was too narrow to hold anything but
//  two progress bars. CSS Grid, one/two/three per row, which is what the props
//  were asking for in the first place and leaves room for the specialty
//  breakdown.
//
//  BREAKDOWN. Facilities run many insurers side by side — the claims entry
//  screen's Insurance Type list is built from DISTINCT scheme_name in
//  claim_information, and a live facility shows a dozen of them. So each card
//  carries a per-insurer split, and the whole dashboard can be filtered down to
//  a single scheme. Specialty is available as the alternate breakdown for the
//  case where one insurer dominates and the interesting variation is clinical.

// Distinct enough to tell five bars apart, muted enough not to become the
// decoration the old gradients were. First two are the brand pair.
const SPECIALTY_COLORS = [
  BRAND.navy, BRAND.green, BRAND.info, BRAND.warning, '#7A5AF8', '#0E926A',
];

const MONTH_TITLE = (m) => (m ? m.charAt(0) + m.slice(1).toLowerCase() : '');

const pct = (value, total) => (total > 0 ? (value / total) * 100 : 0);

// Shared styling for the two header pickers, which sit on the navy band.
const selectSx = {
  color: '#fff',
  fontWeight: 700,
  fontSize: 13.5,
  borderRadius: '10px',
  background: 'rgba(255,255,255,.12)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.28)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.5)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
  '& .MuiSvgIcon-root': { color: '#fff' },
};

const Home = () => {
  const [claimYears, setClaimYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [monthlyClaims, setMonthlyClaims] = useState([]);
  const [loadingYears, setLoadingYears] = useState(true);
  const [loadingClaims, setLoadingClaims] = useState(false);

  // Which scheme the whole dashboard is scoped to, and which dimension the
  // cards break down by.
  const [insurances, setInsurances] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState('all');

  // ── Facility scope ────────────────────────────────────────────────────────
  //  Every claim that arrives by XML import belongs to a named client facility.
  //  A bureau works several of them in the same month, so this is the scope for
  //  the whole screen AND for the drill-down behind it: 'all', 'own' (claims typed
  //  in here), or a client_facilities id.
  //
  //  Held as a string throughout, including for numeric ids, so 'all' / 'own' and
  //  an id are one type. The API parses it and rejects anything else by matching
  //  nothing rather than widening the scope.
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [breakdown, setBreakdown] = useState('insurance'); // 'insurance' | 'specialty'

  // The month drill-down is a VIEW, not a dialog. Opening it swaps the whole
  // panel for the claims table, the way the Claims tab itself behaves, so the
  // month switcher and the back control live in normal page chrome instead of
  // a modal that has to be dismissed before you can look at another month.
  const [activeMonth, setActiveMonth] = useState(null);
  // The schemes chosen for the month being opened. Set when View claims is
  // clicked, so the drill-down knows exactly what to filter by.
  const [activeInsurances, setActiveInsurances] = useState([]);

  // ── Per-month insurance picks ─────────────────────────────────────────────
  //  Keyed by month number, not a single shared set: a facility looks at April's
  //  NHIS and May's Premier in the same session, and one shared selection would
  //  silently carry the wrong scheme from one card to the next.
  //
  //  An unticked month means NO restriction, not "nothing" — an officer who has
  //  not touched the boxes gets the whole month, which is what the button did
  //  before these existed.
  const [picks, setPicks] = useState({});   // { [month_no]: string[] }

  const isInsuranceMode = breakdown === 'insurance';

  const isPicked = (monthNo, name) => (picks[monthNo] || []).includes(name);

  const togglePick = (monthNo, name) => {
    setPicks((prev) => {
      const current = prev[monthNo] || [];
      const next = current.includes(name)
        ? current.filter((n) => n !== name)
        : [...current, name];
      return { ...prev, [monthNo]: next };
    });
  };

  // What View claims will actually filter by. [] means every scheme.
  const picksFor = (monthNo) => picks[monthNo] || [];

  const openMonth = (monthData) => {
    setActiveInsurances(isInsuranceMode ? picksFor(monthData.month_no) : []);
    setActiveMonth(monthData);
  };

  useEffect(() => {
    const loadClaimYears = async () => {
      try {
        setLoadingYears(true);
        const res = await axios.get(claimyears);
        if (res.data.success) {
          setClaimYears(res.data.years);
          setSelectedYear(res.data.years[0]);
        }
      } catch (err) {
        console.error('Failed to load claim years', err);
      } finally {
        setLoadingYears(false);
      }
    };
    loadClaimYears();
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    const loadMonthlyClaims = async () => {
      try {
        setLoadingClaims(true);
        const res = await axios.get(`${claimsmonthly}/${selectedYear}`, {
          params: { insurance: selectedInsurance, facility: selectedFacility },
        });
        if (res.data.success) {
          setMonthlyClaims(res.data.data || []);
          // The scheme list is unfiltered, so choosing one does not remove the
          // others from the picker and strand you on a single scheme.
          setInsurances(res.data.insurances || []);
          // Same for facilities — the picker lists them all whichever is chosen.
          setFacilities(res.data.facilities || []);
        }
      } catch (err) {
        console.error('Failed loading monthly claims', err);
        setMonthlyClaims([]);
      } finally {
        setLoadingClaims(false);
      }
    };
    loadMonthlyClaims();
  }, [selectedYear, selectedInsurance, selectedFacility]);

  // The chosen facility's own label, for the heading and the drill-down. Falls
  // back to the raw value so a facility that has claims but no client_facilities
  // row is still named rather than showing blank.
  const facilityLabel = useMemo(() => {
    if (selectedFacility === 'all') return 'All facilities';
    const hit = facilities.find((f) => String(f.id) === String(selectedFacility));
    return hit ? hit.name : `Facility #${selectedFacility}`;
  }, [selectedFacility, facilities]);

  // What to state under the heading. With 'all' chosen and only ONE facility in
  // the year, the honest label is that facility's name — not "All facilities",
  // which reads as a mixed view when nothing is mixed. This is the case that left
  // the screen silent: one facility, no explicit filter, so neither branch fired.
  const facilityBanner = useMemo(() => {
    if (selectedFacility !== 'all') {
      const hit = facilities.find((f) => String(f.id) === String(selectedFacility));
      return { name: facilityLabel, facilityId: hit ? hit.facilityId : null, sole: true };
    }
    if (facilities.length === 1) {
      return { name: facilities[0].name, facilityId: facilities[0].facilityId, sole: true };
    }
    if (facilities.length > 1) {
      return { name: `${facilities.length} facilities`, facilityId: null, sole: false };
    }
    return null;
  }, [selectedFacility, facilities, facilityLabel]);

  const stats = useMemo(() => {
    const total = monthlyClaims.reduce((acc, m) => acc + m.total, 0);
    const peak = monthlyClaims.length ? Math.max(...monthlyClaims.map((m) => m.total)) : 0;
    return {
      total,
      avg: monthlyClaims.length ? Math.round(total / monthlyClaims.length) : 0,
      peak,
    };
  }, [monthlyClaims]);

  // ── Month drill-down ──────────────────────────────────────────────────────
  //  ClaimsMenu replaces the old ClaimsDetails table. The old one read
  //  claim_information only, so a month of imported claims rendered "No claims
  //  found" under a header that correctly said 2,383.
  if (activeMonth) {
    return (
      <ClaimsMenu
        monthData={activeMonth}
        selectedYear={selectedYear}
        onBack={() => setActiveMonth(null)}
        // The schemes ticked on the month card. Empty means every scheme — but the
        // dashboard's own dropdown still applies, so a dashboard scoped to Premier
        // does not open a table of everything.
        insurances={
          activeInsurances.length
            ? activeInsurances
            : (selectedInsurance !== 'all' ? [selectedInsurance] : [])
        }
        // Carried into the drill-down, not re-chosen there. Opening a month from a
        // dashboard scoped to one facility must not hand you a table of everyone's
        // claims — every action on that screen works from the rows it fetches.
        facility={selectedFacility}
        facilityLabel={facilityLabel}
      />
    );
  }

  if (loadingYears) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={44} sx={{ color: BRAND.navy }} />
          <Typography sx={{ mt: 2, fontSize: 13.5, color: BRAND.inkMuted, fontWeight: 600 }}>
            Loading claims dashboard…
          </Typography>
        </Box>
      </Box>
    );
  }

  const statTiles = [
    { key: 'total', label: 'Total claims', value: stats.total, Icon: FileText },
    { key: 'avg',   label: 'Avg / month',  value: stats.avg,   Icon: Activity },
    { key: 'peak',  label: 'Peak month',   value: stats.peak,  Icon: TrendingUp },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
      {/* ── Header ───────────────────────────────────────────────────────────
          One navy band carrying the title, the year picker and the three
          figures. Previously this was a white slab with the title in a purple
          gradient text-fill and three separate gradient tiles beneath it — five
          competing colours before any data appeared. */}
      <Box
        sx={{
          borderRadius: '16px',
          p: { xs: 2.25, sm: 3 },
          mb: { xs: 2, sm: 3 },
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
          color: '#fff',
          boxShadow: `0 10px 30px ${BRAND.navy}2E`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 800, letterSpacing: '-.4px', lineHeight: 1.2 }}>
              Claims Dashboard
            </Typography>
            <Typography sx={{ fontSize: 12.5, opacity: .78, mt: .4 }}>
              Monthly claim volume for {selectedYear} — select a month to open its claims
            </Typography>
            {/* Says whose claims these are whenever it is not everyone's. The
                figures below change completely with this selector, so leaving it
                to be read off a dropdown at the other end of the header is how a
                facility's total gets mistaken for the bureau's. */}
            {facilityBanner && (
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: .625, mt: .875,
                px: 1, py: .375, borderRadius: '7px',
                background: 'rgba(255,255,255,.14)',
                border: '1px solid rgba(255,255,255,.22)',
              }}>
                <Building2 size={13} style={{ opacity: .9, flexShrink: 0 }} />
                <Box component="span" sx={{ fontSize: 12, fontWeight: 700 }}>
                  {facilityBanner.name}
                </Box>
                {facilityBanner.facilityId != null && (
                  <Box component="span" sx={{ fontSize: 11.5, opacity: .72 }}>
                    #{facilityBanner.facilityId}
                  </Box>
                )}
                {!facilityBanner.sole && (
                  <Box component="span" sx={{ fontSize: 11.5, opacity: .72 }}>
                    — pick one to work on it
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.25, flexShrink: 0, flexWrap: 'wrap' }}>
            {/* Facility. First of the three because it is the widest scope: it
                decides which claims the other two are then narrowing.

                Shown whenever there is ANY facility, not only when there are
                several. Hiding it on a single-facility install was wrong: "which
                facility is this?" is exactly the question an officer opening the
                screen has, and "there is only one" is a useful answer that a
                hidden control cannot give. */}
            {facilities.length > 0 && (
              <FormControl sx={{ minWidth: { xs: '100%', sm: 236 } }} size="small">
                <Select
                  value={selectedFacility}
                  onChange={(e) => setSelectedFacility(e.target.value)}
                  disabled={loadingClaims}
                  startAdornment={<Building2 size={16} style={{ marginRight: 8, opacity: .85, flexShrink: 0 }} />}
                  MenuProps={{ PaperProps: { sx: { borderRadius: '10px', mt: .5, maxHeight: 340 } } }}
                  sx={selectSx}
                  renderValue={() => facilityLabel}
                >
                  <MenuItem value="all">
                    All facilities
                    <Box component="span" sx={{ ml: 1, color: BRAND.inkFaint, fontSize: 12 }}>
                      ({facilities.length})
                    </Box>
                  </MenuItem>
                  {facilities.map((f) => (
                    <MenuItem key={String(f.id)} value={String(f.id)}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                        <Box component="span" sx={{ minWidth: 0 }}>
                          {f.name}
                          {/* The id, beside the name. A bureau's officers refer to
                              facilities by number as often as by name, and the
                              number is what appears in the XML. */}
                          {f.facilityId != null && (
                            <Box component="span" sx={{ ml: .625, color: BRAND.inkFaint, fontSize: 11.5 }}>
                              #{f.facilityId}
                            </Box>
                          )}
                          {/* A facility with claims but no onboarding record still
                              appears — hiding it would hide its claims from the
                              only screen that counts them. */}
                          {f.known === false && (
                            <Box component="span" sx={{ ml: .75, color: BRAND.warning, fontSize: 11 }}>
                              not onboarded
                            </Box>
                          )}
                        </Box>
                        <Box component="span" sx={{ color: BRAND.inkFaint, fontSize: 12, flexShrink: 0 }}>
                          {f.total.toLocaleString()}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Insurance scheme. The list can run to a dozen entries on a live
                facility, so the menu is height-capped rather than opening as a
                full-page slab. */}
            <FormControl sx={{ minWidth: { xs: '100%', sm: 232 } }} size="small">
              <Select
                value={selectedInsurance}
                onChange={(e) => setSelectedInsurance(e.target.value)}
                disabled={loadingClaims}
                startAdornment={<Shield size={16} style={{ marginRight: 8, opacity: .85, flexShrink: 0 }} />}
                MenuProps={{ PaperProps: { sx: { borderRadius: '10px', mt: .5, maxHeight: 340 } } }}
                sx={selectSx}
                renderValue={(v) => (v === 'all' ? 'All insurances' : v)}
              >
                <MenuItem value="all">
                  All insurances
                  <Box component="span" sx={{ ml: 1, color: BRAND.inkFaint, fontSize: 12 }}>
                    ({insurances.length || '—'})
                  </Box>
                </MenuItem>
                {insurances.map((ins) => (
                  <MenuItem key={ins.name} value={ins.name}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <Box component="span" sx={{ minWidth: 0 }}>{ins.name}</Box>
                      <Box component="span" sx={{ color: BRAND.inkFaint, fontSize: 12, flexShrink: 0 }}>
                        {ins.total.toLocaleString()}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: { xs: '100%', sm: 148 } }} size="small">
              <Select
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(e.target.value)}
                disabled={loadingClaims}
                startAdornment={<Calendar size={16} style={{ marginRight: 8, opacity: .85, flexShrink: 0 }} />}
                MenuProps={{ PaperProps: { sx: { borderRadius: '10px', mt: .5 } } }}
                sx={selectSx}
              >
                {claimYears.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Active scheme is stated in words as well as in the picker — a
            filtered dashboard that looks identical to an unfiltered one is how
            a partial figure gets read as the facility total. */}
        {selectedInsurance !== 'all' && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, mt: 2,
              px: 1.5, py: 1, borderRadius: '10px',
              background: 'rgba(255,255,255,.12)',
              border: '1px solid rgba(255,255,255,.2)',
            }}
          >
            <Filter size={14} style={{ flexShrink: 0, opacity: .9 }} />
            <Typography sx={{ fontSize: 12.5, minWidth: 0 }} noWrap>
              Showing <b>{selectedInsurance}</b> only
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={() => setSelectedInsurance('all')}
              sx={{
                ml: 'auto', flexShrink: 0,
                px: 1.25, py: .375, borderRadius: '20px',
                cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 700,
                color: BRAND.navy, background: '#fff', border: 0,
                '&:hover': { background: BRAND.navySoft },
              }}
            >
              Clear
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(3, minmax(0,1fr))' },
            gap: { xs: 1, sm: 1.5 },
            mt: { xs: 2.25, sm: 2.75 },
          }}
        >
          {statTiles.map(({ key, label, value, Icon }) => (
            <Box
              key={key}
              sx={{
                px: { xs: 1.25, sm: 1.75 }, py: { xs: 1.25, sm: 1.5 },
                borderRadius: '11px', minWidth: 0,
                background: 'rgba(255,255,255,.10)',
                border: '1px solid rgba(255,255,255,.16)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: .5, opacity: .82 }}>
                <Icon size={14} />
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase' }} noWrap>
                  {label}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: { xs: 20, sm: 25 }, fontWeight: 800, lineHeight: 1.1 }}>
                {loadingClaims ? '—' : value.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Breakdown switch ─────────────────────────────────────────────── */}
      {!loadingClaims && monthlyClaims.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: BRAND.inkMuted }}>
            Break each month down by
          </Typography>
          <Box
            sx={{
              display: 'flex', gap: .25, p: .375,
              borderRadius: '10px',
              background: BRAND.canvas,
              border: `1px solid ${BRAND.line}`,
            }}
          >
            {[
              { key: 'insurance', label: 'Insurance' },
              { key: 'specialty', label: 'Specialty' },
            ].map((opt) => {
              const on = breakdown === opt.key;
              return (
                <Box
                  key={opt.key}
                  component="button"
                  type="button"
                  onClick={() => setBreakdown(opt.key)}
                  aria-pressed={on}
                  sx={{
                    px: 1.5, py: .625, borderRadius: '7px',
                    border: 0, cursor: 'pointer', font: 'inherit',
                    fontSize: 12.5, fontWeight: on ? 800 : 600,
                    color: on ? BRAND.navy : BRAND.inkMuted,
                    background: on ? '#fff' : 'transparent',
                    boxShadow: on ? '0 1px 3px rgba(15,31,61,.12)' : 'none',
                    '&:hover': { color: BRAND.navy },
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── Month cards ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.75, sm: 2.25 },
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {loadingClaims ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={330} sx={{ borderRadius: '16px' }} />
          ))
        ) : monthlyClaims.length === 0 ? (
          <Box
            sx={{
              gridColumn: '1 / -1',
              p: 6, textAlign: 'center',
              borderRadius: '16px',
              background: '#fff',
              border: `1px solid ${BRAND.line}`,
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: BRAND.ink }}>
              No claims recorded for {selectedYear}
            </Typography>
            <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, mt: .75 }}>
              Pick another year above.
            </Typography>
          </Box>
        ) : (
          monthlyClaims.map((monthData) => {
            const isPeak = monthData.total === stats.peak && stats.peak > 0;
            const outPct = pct(monthData.outpatient, monthData.total);
            const inPct  = pct(monthData.inpatient, monthData.total);

            // Both breakdowns arrive as [{ name|specialty, total }] already
            // sorted largest-first by the server.
            const series = (breakdown === 'insurance'
              ? (monthData.insurances || []).map((i) => ({ label: i.name, total: i.total }))
              : (monthData.specialties || []).map((s) => ({ label: s.specialty, total: s.total }))
            );
            const shown = series.slice(0, 5);
            const restCount = series.slice(5).reduce((n, s) => n + s.total, 0);
            const topValue = shown.length ? shown[0].total : 0;

            return (
              <Box
                key={monthData.month_no ?? monthData.month}
                sx={{
                  display: 'flex', flexDirection: 'column',
                  borderRadius: '16px',
                  background: '#fff',
                  border: `1px solid ${isPeak ? BRAND.green : BRAND.line}`,
                  boxShadow: '0 4px 20px rgba(15,31,61,.06)',
                  overflow: 'hidden',
                  transition: 'transform .18s ease, box-shadow .18s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 14px 34px rgba(15,31,61,.12)',
                  },
                }}
              >
                {/* Month + total. A slim band rather than the old 120px
                    gradient slab that pushed the actual data below the fold. */}
                <Box
                  sx={{
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                    gap: 1, px: 2.25, py: 1.75,
                    borderBottom: `1px solid ${BRAND.line}`,
                    background: isPeak ? BRAND.greenSoft : BRAND.canvas,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 11, fontWeight: 800, letterSpacing: '1px',
                        textTransform: 'uppercase',
                        color: isPeak ? BRAND.greenDark : BRAND.inkFaint,
                      }}
                      noWrap
                    >
                      {MONTH_TITLE(monthData.month)} {selectedYear}
                      {/* Whose month this is, on the card itself. The question
                          standing in front of the View claims button is "which
                          facility am I about to open", and the card could not
                          answer it. One facility gets named with its id; several
                          get counted, because listing four names here would crowd
                          out the figure the card exists to show. */}
                      {(monthData.facilities || []).length === 1 ? (
                        <Box component="span" sx={{
                          ml: .875, color: BRAND.navy, fontWeight: 800,
                          textTransform: 'none', letterSpacing: 0, fontSize: 11.5,
                        }}>
                          · {monthData.facilities[0].name}
                          {monthData.facilities[0].facilityId != null
                            && ` #${monthData.facilities[0].facilityId}`}
                        </Box>
                      ) : (monthData.facilities || []).length > 1 ? (
                        <Box component="span" sx={{
                          ml: .875, color: BRAND.warning, fontWeight: 800,
                          textTransform: 'none', letterSpacing: 0, fontSize: 11.5,
                        }}>
                          · {monthData.facilities.length} facilities
                        </Box>
                      ) : null}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: .75, mt: .25 }}>
                      <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.05, color: BRAND.ink }}>
                        {monthData.total.toLocaleString()}
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: BRAND.inkFaint }}>
                        claims
                      </Typography>
                    </Box>
                  </Box>

                  {isPeak && (
                    <Typography
                      sx={{
                        flexShrink: 0, fontSize: 10, fontWeight: 800, letterSpacing: '.5px',
                        textTransform: 'uppercase',
                        px: 1, py: .375, borderRadius: '20px',
                        background: BRAND.green, color: '#fff',
                      }}
                    >
                      Peak
                    </Typography>
                  )}
                </Box>

                <Box sx={{ px: 2.25, py: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* One stacked bar instead of two separate progress bars.
                      Out and In are parts of the same whole, so showing them as
                      two independent tracks made a 73/27 split read as two
                      unrelated measurements. */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: BRAND.inkFaint, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        Out-patient
                      </Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: BRAND.navy, lineHeight: 1.3 }}>
                        {monthData.outpatient.toLocaleString()}
                        <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: BRAND.inkFaint, ml: .625 }}>
                          {outPct.toFixed(1)}%
                        </Box>
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0, textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: BRAND.inkFaint, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        In-patient
                      </Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: BRAND.green, lineHeight: 1.3 }}>
                        {monthData.inpatient.toLocaleString()}
                        <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: BRAND.inkFaint, ml: .625 }}>
                          {inPct.toFixed(1)}%
                        </Box>
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex', height: 9, borderRadius: '6px',
                      overflow: 'hidden', background: BRAND.line, mb: 2.25,
                    }}
                  >
                    <Box sx={{ width: `${outPct}%`, background: BRAND.navy }} />
                    <Box sx={{ width: `${inPct}%`, background: BRAND.green }} />
                  </Box>

                  {/* Specialty mix */}
                  <Typography
                    sx={{
                      fontSize: 10, fontWeight: 800, letterSpacing: '.9px',
                      textTransform: 'uppercase', color: BRAND.inkFaint, mb: 1.25,
                    }}
                  >
                    By {breakdown === 'insurance' ? 'insurance' : 'specialty'}
                  </Typography>

                  {shown.length === 0 ? (
                    <Typography sx={{ fontSize: 12, color: BRAND.inkFaint, mb: 2 }}>
                      Nothing recorded for this month.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                      {shown.map((s, i) => (
                        <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          {/* ── Insurance pickers ────────────────────────────
                              A facility runs several schemes at once (NHIS,
                              Premier, Apex, Nationwide). Ticking one or more here
                              carries straight through to the claims table, and the
                              table prints the chosen names in its heading — so it
                              is never a guess which scheme's claims are on screen.

                              Only offered on the insurance breakdown. Specialty is
                              a property of a claim, not a book to work through, and
                              the claims table filters by it with its own pills. */}
                          {isInsuranceMode && (
                            <Box
                              component="input"
                              type="checkbox"
                              checked={isPicked(monthData.month_no, s.label)}
                              onChange={() => togglePick(monthData.month_no, s.label)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Filter ${MONTH_TITLE(monthData.month)} claims to ${s.label}`}
                              title={`Include ${s.label} when viewing this month's claims`}
                              sx={{
                                flexShrink: 0, width: 15, height: 15, m: 0,
                                cursor: 'pointer', accentColor: BRAND.green,
                              }}
                            />
                          )}
                          <Typography
                            title={`${s.label} — ${s.total.toLocaleString()} (${pct(s.total, monthData.total).toFixed(1)}% of the month)`}
                            sx={{
                              flex: isInsuranceMode ? '0 0 34%' : '0 0 40%',
                              minWidth: 0, fontSize: 11.5,
                              fontWeight: isPicked(monthData.month_no, s.label) ? 800 : 600,
                              color: isPicked(monthData.month_no, s.label) ? BRAND.ink : BRAND.inkMuted,
                            }}
                            noWrap
                          >
                            {s.label}
                          </Typography>
                          {/* Bars are scaled against the month's TOP specialty,
                              not its total — otherwise every bar but the first
                              is a sliver and the comparison is unreadable. */}
                          <Box sx={{ flex: 1, minWidth: 0, height: 6, borderRadius: '4px', background: BRAND.canvas }}>
                            <Box
                              sx={{
                                width: `${pct(s.total, topValue)}%`,
                                height: '100%', borderRadius: '4px',
                                background: SPECIALTY_COLORS[i % SPECIALTY_COLORS.length],
                              }}
                            />
                          </Box>
                          <Typography sx={{ flexShrink: 0, fontSize: 11.5, fontWeight: 800, color: BRAND.ink, minWidth: 30, textAlign: 'right' }}>
                            {s.total.toLocaleString()}
                          </Typography>
                        </Box>
                      ))}
                      {restCount > 0 && (
                        <Typography sx={{ fontSize: 11, color: BRAND.inkFaint, mt: .25 }}>
                          + {series.length - shown.length} more ({restCount.toLocaleString()} claims)
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* What the button is about to do, spelled out. The officer sees
                      the scheme names before clicking, not after. */}
                  {isInsuranceMode && picksFor(monthData.month_no).length > 0 && (
                    <Typography sx={{
                      fontSize: 11, color: BRAND.greenDark, fontWeight: 700,
                      mb: 1, lineHeight: 1.45,
                    }}>
                      Filtering to {picksFor(monthData.month_no).join(' + ')}
                      {' · '}
                      {series
                        .filter((s) => picksFor(monthData.month_no).includes(s.label))
                        .reduce((sum, s) => sum + s.total, 0)
                        .toLocaleString()} claims
                    </Typography>
                  )}

                  <Box
                    component="button"
                    type="button"
                    onClick={() => openMonth(monthData)}
                    sx={{
                      mt: 'auto', width: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75,
                      px: 2, py: 1.125, borderRadius: '10px',
                      cursor: 'pointer', font: 'inherit',
                      border: `1px solid ${BRAND.line}`,
                      background: BRAND.surface,
                      color: BRAND.navy,
                      fontSize: 13, fontWeight: 700,
                      transition: 'background .15s ease, border-color .15s ease',
                      '&:hover': { background: BRAND.navy, borderColor: BRAND.navy, color: '#fff' },
                      '&:focus-visible': { outline: `2px solid ${BRAND.green}`, outlineOffset: 2 },
                    }}
                  >
                    {isInsuranceMode && picksFor(monthData.month_no).length > 0
                      ? `View ${picksFor(monthData.month_no).length} scheme${picksFor(monthData.month_no).length === 1 ? '' : 's'}`
                      : 'View claims'}
                    <ChevronRight size={16} />
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default Home;
